import { Grid, Divider, TextField, Typography, Box } from '@mui/material'
import React, { useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { LineItem, ShopCheckoutSideBarResponse } from '../../models'
import { useShopCart } from '../../hooks'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { LoadingButton } from '@mui/lab'
import { shopNotificationKeys } from '../../utils'
import { useTypedSelector } from 'src/State/RootReducer'

interface ConfirmCheckoutPromotionCodeProps {
  fullWidth?: boolean
  showLabel?: boolean
  showErrorMessage?: boolean
  appliedPromotions?: LineItem[]
  onPromotionCodeUpdate: (cartData: ShopCheckoutSideBarResponse | null) => void
}

const useStyles = makeStyles()(theme => ({
  promoCodeContainer: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(3),
  },
  textField: {
    marginRight: theme.spacing(1),
    paddingLeft: 0,
  },
  normalText: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.grey.grey03,
  },
  inputContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}))

/*
 * This Component shows "Promotion Code" inputfield and button with functionality on the Confirm Checkout.
 */
const ConfirmCheckoutPromotionCode: React.FC<ConfirmCheckoutPromotionCodeProps> = ({
  onPromotionCodeUpdate,
  appliedPromotions,
  fullWidth = false,
  showLabel = false,
  showErrorMessage = false,
}) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { addPromotionCodeCart, isLoading } = useShopCart()
  const { enqueueErrorStack } = useSnackbar()
  const [codeText, setCodeText] = useState<string>('')
  /*TODO: Remove this and the functionality later again, Fix the problem with Snackbar.*/
  const [message, setMessage] = useState<string | null>(null)
  const [messageType, setMessageType] = useState<'error' | 'success'>('success')
  const cartQuantity = useTypedSelector(state => state.shop.basketQuantity)
  const disableAddPromotion = cartQuantity <= 0 || !codeText.trim()

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCodeText(e.target.value)
    setMessage('')
  }

  const handlePromotionCode = (codeText: string): void => {
    if (appliedPromotions?.find(promotionItem => promotionItem.payload?.code === codeText)) {
      setMessage('shop.confirm-checkout.promotion-code-already-in-basket')
      setMessageType('error')
      return enqueueErrorStack(t('shop.confirm-checkout.promotion-code-already-in-basket'))
    }
    addPromotionCodeCart(codeText)
      .then(result => {
        if (!result) {
          setMessage('shop.confirm-checkout.promotion-code-fail-label')
          setMessageType('error')
          enqueueErrorStack(t('shop.confirm-checkout.promotion-code-fail-label'))
        }
        onPromotionCodeUpdate(result)
        if (result?.errors && Object.keys(result.errors).length > 0) {
          Object.keys(result.errors).forEach(errorKey => {
            const errorMessage = result.errors[errorKey]
            if (shopNotificationKeys.some(pattern => errorKey.includes(pattern))) {
              setMessage(`shop.error.${errorMessage.messageKey}`)
              setMessageType('success')
            } else {
              setMessage(`shop.error.${errorMessage.messageKey}`)
              setMessageType('error')
            }
          })
        }
      })
      .catch(err => {
        setMessage('shop.confirm-checkout.promotion-code-fail-label')
        setMessageType('error')
        enqueueErrorStack(t('shop.confirm-checkout.promotion-code-fail-label'))
        console.error(err)
      })
  }

  return (
    <Grid container spacing={3} alignItems="center" justifyContent="center" className={classes.promoCodeContainer}>
      {!fullWidth && <Grid item xs={12} md={6} />}
      <Grid item xs={12} md={fullWidth ? 12 : 6}>
        {showLabel && (
          <>
            <Typography variant="h2" sx={{ fontWeight: 400 }} className={classes.normalText}>
              {t('shop.confirm-checkout.promotion-code-label')}
            </Typography>
            <Divider className={classes.normalText} variant="fullWidth" />
          </>
        )}
        <Box className={classes.inputContainer}>
          <TextField
            className={classes.textField}
            fullWidth
            label={t('shop.confirm-checkout-promotion-code-placeholder')}
            value={codeText}
            onChange={handleCodeChange}
          />
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={() => codeText && handlePromotionCode(codeText)}
            disabled={disableAddPromotion}
            loading={isLoading}
          >
            {t('shop.confirm-checkout.promotion-apply-code')}
          </LoadingButton>
        </Box>
        {message && showErrorMessage && (
          <Typography variant="subtitle2" color={messageType === 'error' ? 'error' : 'primary'}>
            {t(message)}
          </Typography>
        )}
      </Grid>
    </Grid>
  )
}

export default ConfirmCheckoutPromotionCode
